import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Send, RefreshCw, Mail, Clock, Wand2 } from "lucide-react";
import { toast } from "sonner";
import {
  getCoachingAthlete,
  getAthleteReportCardPreviewUrl,
  sendAthleteReportCard,
  getAthleteReportCardLastSent,
  getAthleteReportCardDraft,
} from "../../lib/api";
import { ReportCardConfirmDialog } from "../../components/coaching/ReportCardConfirmDialog";

function fmtAgo(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// Debounce hook — the preview iframe refreshes ~400 ms after the coach stops
// typing so we don't slam the backend on every keystroke.
function useDebounced(value, delay = 400) {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(t);
  }, [value, delay]);
  return debounced;
}

export default function ReportCardComposerPage() {
  const { id } = useParams();
  const [athlete, setAthlete] = useState(null);
  const [headline, setHeadline] = useState("");
  const [wentWell, setWentWell] = useState("");
  const [focusNext, setFocusNext] = useState("");
  const [notes, setNotes] = useState("");
  // Custom report-card date range. `endWeekStart` is the ISO date of the
  // Monday of the LAST week we want to include; `weeksBack` is how many
  // weeks (1–12) to summarise. Defaults reproduce the historical "rolling
  // four weeks ending today" behaviour, but the coach can pick any window
  // — e.g. send a report on a Monday summarising Mon-3 weeks ago → Sun-1
  // week ago so the half-empty current week doesn't drag adherence down.
  const [endWeekStart, setEndWeekStart] = useState(() => {
    // Default to the Monday of the PREVIOUS completed week (so a report
    // sent on Monday morning doesn't include this half-empty week).
    const today = new Date();
    const dow = today.getDay(); // 0=Sun..6=Sat
    const daysSinceMonday = dow === 0 ? 6 : dow - 1;
    const thisMonday = new Date(today);
    thisMonday.setDate(today.getDate() - daysSinceMonday);
    const lastMonday = new Date(thisMonday);
    lastMonday.setDate(thisMonday.getDate() - 7);
    return `${lastMonday.getFullYear()}-${String(lastMonday.getMonth() + 1).padStart(2, "0")}-${String(lastMonday.getDate()).padStart(2, "0")}`;
  });
  const [weeksBack, setWeeksBack] = useState(4);
  const [sending, setSending] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [lastSent, setLastSent] = useState(null);
  const [previewKey, setPreviewKey] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const iframeRef = useRef(null);

  useEffect(() => {
    getCoachingAthlete(id)
      .then(setAthlete)
      .catch(() => toast.error("Couldn't load athlete"));
    getAthleteReportCardLastSent(id)
      .then(setLastSent)
      .catch(() => setLastSent(null));
  }, [id]);

  // Debounced query string drives the preview src so live edits don't slam
  // the backend on every keypress.
  const debouncedHeadline = useDebounced(headline);
  const debouncedWentWell = useDebounced(wentWell);
  const debouncedFocusNext = useDebounced(focusNext);
  const debouncedNotes = useDebounced(notes);
  const debouncedEndWeek = useDebounced(endWeekStart, 200);
  const debouncedWeeksBack = useDebounced(weeksBack, 200);
  const previewSrc = useMemo(
    () =>
      getAthleteReportCardPreviewUrl(id, {
        headline: debouncedHeadline,
        went_well: debouncedWentWell,
        focus_next: debouncedFocusNext,
        notes: debouncedNotes,
        end_week_start: debouncedEndWeek,
        weeks_back: debouncedWeeksBack,
        cache_bust: previewKey,
      }),
    [id, debouncedHeadline, debouncedWentWell, debouncedFocusNext, debouncedNotes, debouncedEndWeek, debouncedWeeksBack, previewKey],
  );

  const onAutoDraft = async () => {
    // Only confirm if the coach already typed something — otherwise just fill.
    const dirty = (headline + wentWell + focusNext + notes).trim().length > 0;
    if (
      dirty &&
      !window.confirm(
        "Replace what you've written with an auto-drafted version from the last 4 weeks of data?",
      )
    ) {
      return;
    }
    setDrafting(true);
    try {
      const draft = await getAthleteReportCardDraft(id);
      setHeadline(draft.headline || "");
      setWentWell(draft.went_well || "");
      setFocusNext(draft.focus_next || "");
      setNotes(draft.notes || "");
      toast.success("Drafted from the last 4 weeks of data — edit as you like.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't auto-draft");
    } finally {
      setDrafting(false);
    }
  };


  const onSend = () => {
    if (!athlete?.email) {
      toast.error("This athlete has no email on file — add one on their profile first.");
      return;
    }
    setConfirmOpen(true);
  };

  const onConfirmSend = async () => {
    setSending(true);
    try {
      const result = await sendAthleteReportCard(id, {
        headline,
        went_well: wentWell,
        focus_next: focusNext,
        notes,
        end_week_start: endWeekStart,
        weeks_back: weeksBack,
      });
      toast.success(
        result.status === "sent"
          ? `Sent to ${result.email}`
          : `Queued (Resend not configured) — coach can preview but no email left the server.`
      );
      setLastSent(result);
      setConfirmOpen(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't send the report card");
    } finally {
      setSending(false);
    }
  };

  if (!athlete) {
    return (
      <div
        className="max-w-7xl mx-auto px-6 py-16 text-center text-[var(--tir-muted)]"
        data-testid="report-card-loading"
      >
        Loading…
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-8" data-testid="report-card-page">
      <Link
        to={`/coaching/athletes/${id}`}
        className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-[var(--tir-muted)] hover:text-[var(--tir-burgundy)] transition-colors mb-4"
      >
        <ArrowLeft size={14} /> Back to {athlete.name}
      </Link>

      <header className="border-b border-[var(--tir-ink)] pb-6 mb-6">
        <div className="text-[11px] tracking-[0.3em] uppercase text-[var(--tir-burgundy)] font-semibold mb-3">
          Coaching · Report Card
        </div>
        <h1 className="font-serif-display text-5xl leading-none tracking-tight">
          Coaching Report Card
        </h1>
        <div className="mt-2 text-sm text-[var(--tir-muted)]">
          Email recipient:{" "}
          <span className="font-mono-tir text-[var(--tir-ink)]">
            {athlete.name}
            {athlete.email ? ` · ${athlete.email}` : " · ⚠ no email on file"}
          </span>
        </div>
        {lastSent?.sent_at && (
          <div className="mt-2 text-[10px] tracking-[0.22em] uppercase text-[var(--tir-muted)] inline-flex items-center gap-2">
            <Clock size={11} />
            Last sent {fmtAgo(lastSent.sent_at)}
          </div>
        )}
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-8" data-testid="report-card-grid">
        {/* Compose panel */}
        <div className="space-y-5">
          <button
            type="button"
            onClick={onAutoDraft}
            disabled={drafting}
            data-testid="report-card-auto-draft-button"
            className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 border-2 border-dashed border-[var(--tir-burgundy)] hover:bg-[var(--tir-burgundy)]/5 disabled:opacity-50 text-[var(--tir-burgundy)] text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors"
            title="Auto-fill from the last 4 weeks of data — you can edit anything before sending"
          >
            <Wand2 size={14} />
            {drafting ? "Drafting…" : "Auto-fill from data"}
          </button>
          <p className="-mt-3 text-[10px] tracking-[0.16em] uppercase text-[var(--tir-muted)] text-center">
            Pulls adherence + volume from the last 4 weeks. Always editable.
          </p>

          {/* Custom date-range picker — lets coach summarise a specific
              historical window instead of the rolling default. */}
          <div
            className="border border-[var(--tir-border)] bg-white p-3 rounded-sm space-y-2"
            data-testid="report-card-range-picker"
          >
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)]">
              Report range
            </div>
            <div className="grid grid-cols-[1fr_auto] gap-2">
              <div>
                <label className="block text-[9px] tracking-[0.16em] uppercase text-[var(--tir-muted)] mb-1">
                  Ending week (Monday)
                </label>
                <input
                  type="date"
                  value={endWeekStart}
                  onChange={(e) => setEndWeekStart(e.target.value)}
                  data-testid="report-card-end-week"
                  className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-2 py-1.5 text-sm focus:outline-none font-mono-tir"
                  title="The Monday of the last week to include. We'll snap to the nearest Monday if you pick mid-week."
                />
              </div>
              <div>
                <label className="block text-[9px] tracking-[0.16em] uppercase text-[var(--tir-muted)] mb-1">
                  Weeks
                </label>
                <select
                  value={weeksBack}
                  onChange={(e) => setWeeksBack(parseInt(e.target.value, 10))}
                  data-testid="report-card-weeks-back"
                  className="bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-2 py-1.5 text-sm focus:outline-none font-mono-tir"
                >
                  {[1, 2, 3, 4, 6, 8, 12].map((n) => (
                    <option key={n} value={n}>{n}</option>
                  ))}
                </select>
              </div>
            </div>
            <p className="text-[10px] tracking-[0.16em] uppercase text-[var(--tir-muted)] leading-snug">
              {(() => {
                try {
                  const end = new Date(endWeekStart + "T00:00:00");
                  const start = new Date(end);
                  start.setDate(end.getDate() - (weeksBack - 1) * 7);
                  const endSun = new Date(end);
                  endSun.setDate(end.getDate() + 6);
                  const fmt = (d) => d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
                  return `Summarising ${fmt(start)} – ${fmt(endSun)}`;
                } catch {
                  return "Pick a Monday and the number of weeks to include.";
                }
              })()}
            </p>
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
              Headline
            </label>
            <input
              type="text"
              value={headline}
              onChange={(e) => setHeadline(e.target.value)}
              placeholder="e.g. Strong base — let's add some intensity"
              data-testid="report-card-headline"
              className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-base focus:outline-none"
            />
            <p className="mt-1 text-[10px] tracking-[0.16em] uppercase text-[var(--tir-muted)]">
              Short, punchy. Renders as the email subheading.
            </p>
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
              What went well
            </label>
            <textarea
              value={wentWell}
              onChange={(e) => setWentWell(e.target.value)}
              placeholder="Highlight wins, consistency, breakthroughs…"
              rows={5}
              data-testid="report-card-went-well"
              className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-sm focus:outline-none leading-relaxed"
            />
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
              Focus for the next {weeksBack} week{weeksBack === 1 ? "" : "s"}
            </label>
            <textarea
              value={focusNext}
              onChange={(e) => setFocusNext(e.target.value)}
              placeholder="Specific cues, intensity targets, recovery focus…"
              rows={5}
              data-testid="report-card-focus-next"
              className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-sm focus:outline-none leading-relaxed"
            />
            <p className="mt-1 text-[10px] tracking-[0.16em] uppercase text-[var(--tir-muted)]">
              Plain text or simple URLs — links autolink in the email.
            </p>
          </div>

          <div>
            <label className="block text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] mb-1">
              Coach's notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="General comments, struggles you noticed, questions to ask the athlete (e.g. 'what got in the way of Tuesdays?'), context to remember…"
              rows={5}
              data-testid="report-card-notes"
              className="w-full bg-white border border-[var(--tir-border)] focus:border-[var(--tir-burgundy)] px-3 py-2 text-sm focus:outline-none leading-relaxed"
            />
            <p className="mt-1 text-[10px] tracking-[0.16em] uppercase text-[var(--tir-muted)]">
              Open-ended space — flag roadblocks, ask questions back. The email already includes a reply/text reminder.
            </p>
          </div>

          <div className="pt-2 border-t border-[var(--tir-border)] flex flex-wrap items-center gap-3">
            <button
              type="button"
              onClick={() => setPreviewKey((k) => k + 1)}
              data-testid="report-card-refresh-preview"
              className="inline-flex items-center gap-2 px-3 py-2 border border-[var(--tir-border)] hover:border-[var(--tir-ink)] text-[10px] tracking-[0.22em] uppercase text-[var(--tir-ink)]"
              title="Force-refresh the preview"
            >
              <RefreshCw size={12} /> Refresh preview
            </button>
            <button
              type="button"
              onClick={onSend}
              disabled={sending || !athlete?.email}
              data-testid="report-card-send-button"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[var(--tir-burgundy)] hover:bg-[var(--tir-burgundy-dark)] disabled:opacity-50 disabled:cursor-not-allowed text-white text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors ml-auto"
            >
              <Send size={13} /> {sending ? "Sending…" : `Send to ${athlete.name.split(" ")[0]}`}
            </button>
          </div>
          <p className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)]">
            Subject: <span className="text-[var(--tir-ink)]">"Coaching Report Card"</span>
          </p>
        </div>

        {/* Preview iframe */}
        <div data-testid="report-card-preview-panel">
          <div className="flex items-baseline justify-between mb-2">
            <div className="text-[10px] tracking-[0.22em] uppercase font-semibold text-[var(--tir-muted)] inline-flex items-center gap-2">
              <Mail size={11} /> Live email preview
            </div>
            <div className="text-[10px] tracking-[0.18em] uppercase text-[var(--tir-muted)]">
              Updates as you type
            </div>
          </div>
          <iframe
            ref={iframeRef}
            src={previewSrc}
            title="Coaching report card preview"
            className="w-full h-[80vh] border border-[var(--tir-border)] bg-white"
            data-testid="report-card-preview-iframe"
          />
        </div>
      </div>
      <ReportCardConfirmDialog
        open={confirmOpen}
        onOpenChange={setConfirmOpen}
        athlete={athlete}
        endWeekStart={endWeekStart}
        weeksBack={weeksBack}
        onConfirm={onConfirmSend}
        sending={sending}
      />
    </div>
  );
}
